import React from "react";

const load = (key: string) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "[]");
  } catch {
    return [];
  }
};

function lastMonths(n: number): string[] {
  const out: string[] = [];
  const d = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

const monthLabel = (m: string) => new Date(m + "-01").toLocaleDateString(undefined, { month: "short" });

export default function Trends({ user }: { user?: any }) {
  const months = lastMonths(6);
  const nearMiss = load("near_miss_records");
  const wounds = load("rmacs_wound_vault");
  const safeguarding = load("rmacs_safeguarding_vault");
  const wellbeing = load("wellbeing_records");

  const countBy = (dates: string[]) => months.map(m => dates.filter(d => (d || "").slice(0, 7) === m).length);

  const nm = countBy(nearMiss.map((i: any) => i.date));
  const wo = countBy(wounds.map((i: any) => i.timestamp));
  const sg = countBy(safeguarding.map((i: any) => i.date));
  const wb = months.map(m => {
    const list = wellbeing.filter((w: any) => (w.month || "") === m);
    if (!list.length) return 0;
    return Math.round((list.reduce((s: number, w: any) => s + (w.mood + w.engagement + w.social + w.appetite) / 4, 0) / list.length) * 10) / 10;
  });

  const charts = [
    { title: "Near misses", data: nm, color: "#2563eb", goodWhenDown: true, suffix: "" },
    { title: "New wound charts", data: wo, color: "#dc2626", goodWhenDown: true, suffix: "" },
    { title: "Safeguarding alerts", data: sg, color: "#7c3aed", goodWhenDown: true, suffix: "" },
    { title: "Average wellbeing (of 5)", data: wb, color: "#16a34a", goodWhenDown: false, suffix: "" },
  ];

  const exportPrint = () => {
    const body = charts.map(c => `<h3 style="color:#123B36">${c.title}</h3><p>${months.map((m, i) => `${monthLabel(m)}: ${c.data[i]}`).join(" | ")}</p>`).join("");
    const html = `<!DOCTYPE html><html><head><title>Trend Dashboard</title><style>body{font-family:Arial;margin:40px;color:#1f2937}</style></head><body><h2 style="color:#123B36">Improvement Trends - Roop Cottage Residential Care Home</h2><p>Generated ${new Date().toLocaleDateString()}. Six-month trend data for CQC inspection.</p>${body}</body></html>`;
    const win = window.open("", "_blank");
    if (win) { win.document.write(html); win.document.close(); win.focus(); win.print(); }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Improvement Trend Dashboard</h2>
          <p className="text-sm text-slate-600">Six-month trends across safety and wellbeing. Outstanding is awarded when improvement is visible over time.</p>
        </div>
        <button onClick={exportPrint} className="rounded-lg bg-red-700 px-4 py-2 text-sm font-medium text-white">Print trend report</button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {charts.map(c => {
          const max = Math.max(1, ...c.data);
          const d = c.data[c.data.length - 1] - c.data[c.data.length - 2];
          const improving = c.goodWhenDown ? d < 0 : d > 0;
          const worsening = c.goodWhenDown ? d > 0 : d < 0;
          return (
            <div key={c.title} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">{c.title}</h3>
                <span className={`text-sm font-semibold ${improving ? "text-green-700" : worsening ? "text-red-700" : "text-slate-600"}`}>
                  {improving ? "▲ Improving" : worsening ? "▼ Worsening" : "● Stable"}
                </span>
              </div>
              <div className="mt-3 flex h-32 items-end gap-2">
                {c.data.map((v, i) => (
                  <div key={months[i]} className="flex flex-1 flex-col items-center gap-1">
                    <div className="text-xs text-slate-600">{v || ""}</div>
                    <div className="w-full rounded-t" style={{ height: `${(v / max) * 100}%`, minHeight: v ? "6px" : "2px", background: c.color }} />
                    <div className="text-xs text-slate-500">{monthLabel(months[i])}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-2 font-semibold text-slate-900">How to read this for inspection</h3>
        <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
          <li>Falling near-miss and wound counts show your prevention work is working.</li>
          <li>A rising wellbeing line shows residents' quality of life is improving, not just maintained.</li>
          <li>Any red "Worsening" trend should link to a Near-Miss learning entry or Action Plan - inspectors want to see the loop closed.</li>
          <li>Print this page each month and file it as governance evidence.</li>
        </ul>
      </div>
    </div>
  );
}